import { t, type ErrorCodeMapperEntry } from "./types.js";

/** Error codes 29000-29999. One thousand-block per file: a duplicate number here can only
 *  collide within this file, which is what makes the split safer than one shared object. */
export const fileContentErrors = {
  // 29xxx is the fileOperations module's series. Four of its sites needed no new code — two reused
  // FILE_NOT_FOUND (25025), one FOLDER_NOT_FOUND (25008) and one FILE_VERSION_NOT_FOUND (25001).
  // One stays generic: it interpolates an uploaded/total count.
  // 29001-29018 — the fileOperations module's split.
  FILE_VERSION_NO_CACHED_SPREADSHEET: {
    errorCode: 29001,
    errorMessage: t("This file version has no cached spreadsheet data."),
    isDynamic: false,
  },
  FILE_NOTE_TEXT_REQUIRED: {
    errorCode: 29002,
    errorMessage: t("No note text was supplied, so nothing was saved."),
    isDynamic: false,
  },
  FILE_NOTE_ACTION_UNSUPPORTED: {
    errorCode: 29003,
    errorMessage: t("Unsupported note action."),
    isDynamic: false,
  },
  // Deliberately undifferentiated: the row is missing OR not writable by this caller.
  FILE_UPDATE_MATCHED_NOTHING: {
    errorCode: 29004,
    errorMessage: t("Nothing was updated — the file was not found, or you cannot modify it."),
    isDynamic: false,
  },
  FILE_NONE_UPLOADED: {
    errorCode: 29006,
    errorMessage: t("No File uploaded."),
    isDynamic: false,
  },
  FILE_NOMENCLATURE_NON_COMPLIANT: {
    errorCode: 29007,
    errorMessage: t("File nomenclature does not comply with the nomenclature rule for its Folder."),
    isDynamic: false,
  },
  FILE_METADATA_NO_CHANGES_SUPPLIED: {
    errorCode: 29008,
    errorMessage: t("No metadata changes were supplied to save."),
    isDynamic: false,
  },
  FILE_METADATA_TEMPLATE_NOT_FOUND: {
    errorCode: 29009,
    errorMessage: t("The Template this metadata belongs to no longer exists."),
    isDynamic: false,
  },
  // 29010-29014: shortcut creation. Each names a different precondition, so each keeps its own code.
  // 29011 (transmittal excludes this Space) is retired: that gate now answers TRANSMITTAL_SHORTCUT_NOT_ALLOWED.
  SHORTCUT_REQUESTING_USER_NOT_FOUND: {
    errorCode: 29010,
    errorMessage: t("Cannot create shortcut: the requesting user no longer exists"),
    isDynamic: false,
  },
  SHORTCUT_DESTINATION_SPACE_NOT_FOUND: {
    errorCode: 29012,
    errorMessage: t("Cannot create shortcut: the destination Space no longer exists"),
    isDynamic: false,
  },
  SHORTCUT_NONE_CREATED: {
    errorCode: 29013,
    errorMessage: t("No shortcuts were created."),
    isDynamic: false,
  },
  SHORTCUT_DUPLICATE_AT_LOCATION: {
    errorCode: 29014,
    errorMessage: t("Cannot create shortcut of same file at this location"),
    isDynamic: false,
  },
  FILE_COPY_NO_RECORD_AT_DESTINATION: {
    errorCode: 29015,
    errorMessage: t("The copy was not written: no file record was created at the destination."),
    isDynamic: false,
  },
  FILE_COPY_FAILED: {
    errorCode: 29016,
    errorMessage: t("The file could not be copied."),
    isDynamic: false,
  },
  FILE_CANNOT_COPY_DIFFERENT_SPACE: {
    errorCode: 29017,
    errorMessage: t("Files can't be copied into a different Space."),
    isDynamic: false,
  },
  FILE_COPY_DESTINATION_SPACE_NOT_FOUND: {
    errorCode: 29018,
    errorMessage: t("Destination Space not found."),
    isDynamic: false,
  },
  // 29019-29023 came off NO_CHANGES_APPLIED (9005). Each is deliberately undifferentiated: the
  // id matched nothing, OR the stored value already equalled the submitted one.
  FILE_VERSION_NOTE_UNCHANGED: {
    errorCode: 29019,
    errorMessage: t("No file version matched this id, or the note already held this text."),
    isDynamic: false,
  },
  FILE_VERSION_NOTE_ABSENT: {
    errorCode: 29020,
    errorMessage: t("No file version matched this id, or it carried no note to delete."),
    isDynamic: false,
  },
  FILE_ANSWERS_UNCHANGED: {
    errorCode: 29021,
    errorMessage: t("No file matched this id, or the answers were already saved with these values."),
    isDynamic: false,
  },
  FILE_NAME_UNCHANGED: {
    errorCode: 29022,
    errorMessage: t("No file matched this id, or it already carried this name."),
    isDynamic: false,
  },
  FILE_METADATA_UNCHANGED: {
    errorCode: 29023,
    errorMessage: t("No metadata was saved: every supplied value already matched what is stored."),
    isDynamic: false,
  },
  FILE_FORM_TEMPLATE_AND_PROJECT_REQUIRED: {
    errorCode: 29024,
    errorMessage: t("formTemplateId and projectId are required"),
    isDynamic: false,
  },
  FILE_NOT_AVAILABLE: {
    errorCode: 29025,
    errorMessage: t("This file is not available!"),
    isDynamic: false,
  },
  // 29026: the escrow residual-files report; the two counts ride in params.
  FILE_UPLOAD_PARTIAL_RESIDUAL: {
    errorCode: 29026,
    errorMessage: t(
      "{{failed}}/{{total}} File(s) uploaded successfully. Please check Notifications or visit Escrow files section.",
    ),
    isDynamic: true,
    dynamicParams: ["failed", "total"],
  },
} satisfies Record<string, ErrorCodeMapperEntry>;
